/**
 * "Şefin kararları" — the chief's decisions.
 *
 * The conductor's desk shows what the chief can *work out*: a ranked plan from
 * rules, deterministic and free. This panel shows what the chief can *judge*.
 * One decision turn reads the whole workforce — every open task, every live
 * session and what it last did, every account's readiness — and a model proposes
 * what should happen next, with a visible reason per action (ADR-0021).
 *
 * Nothing here acts on its own. A turn spends exactly one request on the account
 * an operator designated in Settings, and every action it proposes waits for the
 * approve button. Because approving re-reads the world, a proposal that has gone
 * stale is refused with a reason rather than written blind — which is why this
 * panel shows refusals as prominently as it shows successes.
 *
 * It renders only when the chief is switched on or has decided before: a panel
 * for a feature nobody has enabled is decoration (CLAUDE.md).
 */

import { Link } from 'react-router-dom'
import { Brain } from 'lucide-react'

import { Icon } from '@/components/icon'
import { Badge, Button, Panel, StatusDot } from '@/components/primitives'
import { Notice, useApiErrorMessage } from '@/components/controls'
import { useConfirm } from '@/components/confirm'
import { useT, useLocale } from '@/i18n/context'
import { formatRelative } from '@/i18n'
import { runStateTone, supervisorOutcomeTone } from '@/lib/tone'
import { useSupervisorMutations, useSupervisorStatus } from './workData'
import type { SupervisorTurn } from '@/types/domain'
import './SupervisorPanel.css'

export function SupervisorPanel() {
  const t = useT()
  const { locale } = useLocale()
  const describe = useApiErrorMessage()
  const confirm = useConfirm()
  const status = useSupervisorStatus()
  const { decide, approve } = useSupervisorMutations()

  const data = status.data
  // Read the list defensively. The server always sends an array, but this panel
  // was added to a screen that has to keep working: a shape it did not expect —
  // an older server, a degraded response — must cost the operator this panel,
  // not the session board underneath it.
  const turns = data?.turns ?? []
  // Nothing to show and nothing to switch on: stay out of the way entirely.
  if (!data || (!data.enabled && turns.length === 0)) return null

  const latest = turns[0]
  const proposed = latest && latest.state === 'PLANNED' ? latest : null

  const doApprove = async (turn: SupervisorTurn) => {
    const ok = await confirm({
      title: t('supervisor.approve'),
      message: t('supervisor.approveConfirm', { count: (turn.actions ?? []).length }),
      confirmLabel: t('supervisor.approve'),
      tone: 'default',
    })
    if (!ok) return
    approve.mutate(turn.id)
  }

  return (
    <Panel
      title={t('supervisor.title')}
      className="k-sup"
      actions={
        <Button
          size="sm"
          variant="primary"
          busy={decide.isPending || data.deciding}
          disabled={!data.available || data.deciding}
          onClick={() => decide.mutate()}
        >
          <Icon icon={Brain} size={14} />
          {t('supervisor.decide')}
        </Button>
      }
    >
      <p className="k-sup__lede">{t('supervisor.lede')}</p>

      {(decide.error || approve.error) && (
        <Notice tone="err">{describe(decide.error ?? approve.error).title}</Notice>
      )}

      {/* Why the button is off. The panel says it in advance rather than making
          an operator press it to find out. */}
      {!data.available && data.reason && <Notice tone="warn">{data.reason}</Notice>}

      {data.available && data.accountLabel && (
        <p className="k-sup__account">
          {t('supervisor.runsOn')} <span className="k-mono">{data.accountLabel}</span>
          {data.model && <span className="k-mono"> · {data.model}</span>}
        </p>
      )}

      {/* The timer, when one is set: an operator should never have to wonder
          whether something is deciding on its own. */}
      {data.autonomousEveryMinutes > 0 && (
        <Notice tone="accent">
          {t('supervisor.loopOn', { minutes: data.autonomousEveryMinutes })}
        </Notice>
      )}

      {data.deciding && <Notice tone="info">{t('supervisor.deciding')}</Notice>}

      {proposed && (
        <div className="k-sup__decision">
          {proposed.summary && <p className="k-sup__summary">{proposed.summary}</p>}
          {proposed.justification && (
            <p className="k-sup__because">{proposed.justification}</p>
          )}

          {(proposed.actions ?? []).length === 0 ? (
            <p className="k-sup__empty">{t('supervisor.nothingToDo')}</p>
          ) : (
            <>
              <h3 className="k-label">{t('supervisor.actions', { count: proposed.actions.length })}</h3>
              <ul className="k-sup__list">
                {(proposed.actions ?? []).map((a, i) => (
                  <li key={`${a.taskId}-${i}`} className="k-sup__action">
                    <Badge tone="info" subtle mono>
                      {t(`supervisor.kind.${a.kind}`)}
                    </Badge>
                    <Link to={`/tasks/${a.taskId}`} className="k-sup__task">
                      {a.taskTitle ?? a.taskId}
                    </Link>
                    {a.reason && <span className="k-sup__why">{a.reason}</span>}
                  </li>
                ))}
              </ul>
              <Button variant="primary" busy={approve.isPending} onClick={() => void doApprove(proposed)}>
                {t('supervisor.approve')}
              </Button>
            </>
          )}

          {/* Refusals recorded at decision time: what the chief asked for and
              Kimene declined, and why. */}
          {(proposed.results ?? []).length > 0 && <Outcomes turn={proposed} />}
        </div>
      )}

      {turns.length > 0 && (
        <details className="k-sup__history">
          <summary className="k-sup__historysum">{t('supervisor.history')}</summary>
          <ul className="k-sup__turns">
            {turns.slice(0, 8).map((turn) => (
              <li key={turn.id} className="k-sup__turnrow">
                <StatusDot tone={runStateTone(turn.state)} label={turn.state} />
                <span className="k-sup__turnmeta">
                  {turn.summary || turn.note || t('supervisor.noSummary')}
                  {turn.autonomous && (
                    <span className="k-mono"> · {t('supervisor.autonomous')}</span>
                  )}
                  <span className="k-mono"> · {formatRelative(turn.decidedAt, locale)}</span>
                  {turn.repairTurns > 0 && (
                    <span className="k-mono"> · {t('supervisor.repairs', { count: turn.repairTurns })}</span>
                  )}
                </span>
                {(turn.results ?? []).length > 0 && <Outcomes turn={turn} />}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Panel>
  )
}

/** The outcomes of one turn's actions, each carrying the reason it got there. */
function Outcomes({ turn }: { turn: SupervisorTurn }) {
  const t = useT()
  return (
    <ul className="k-sup__outcomes">
      {(turn.results ?? []).map((res, i) => (
        <li key={`${res.taskId ?? 'none'}-${i}`} className="k-sup__outcome">
          <Badge tone={supervisorOutcomeTone(res.outcome)} subtle mono>
            {t(`supervisor.outcome.${res.outcome}`)}
          </Badge>
          {res.taskTitle && <span className="k-sup__outcometask">{res.taskTitle}</span>}
          {res.detail && <span className="k-sup__outcomewhy">{res.detail}</span>}
        </li>
      ))}
    </ul>
  )
}
